// pfs-text-field.ts — self-contained Text-Control (PFS-619, Epic „Universale Modals").
//
// Anders als <pfs-form-field> (das sein Control NATIV slottet) rendert
// <pfs-text-field> sein <input> in der EIGENEN Shadow. Damit ist das Control
// gegen Dokument-CSS (WP-Preflight/Tailwind) immun — Dokument-Stylesheets
// matchen keine Shadow-Kinder. Folge: openModal@document.body funktioniert ohne
// Durchgriff, egal wo das Modal haengt (der Grund, warum Weg 1 bisher brach).
//
// API:
//   Attribute (data-*): data-label · data-type (text|email|tel|url|number|…) ·
//     data-name · data-autocomplete · data-placeholder · data-required ·
//     data-error · data-hint · data-value (INITIALwert) · data-readonly (zeigt
//     data-value als Wert-Box) · data-inline (kompakte In-Cell-Variante, kein
//     Label/Message).
//   Property: el.value (get/set) — spiegelt den internen Input.
//   Events: das Control re-emittiert `input` + `change` als bubbles+composed vom
//     Host (natives `change` ueberquert die Shadow-Grenze NICHT), damit die
//     reaktive Modal-Gatung + Konsumenten sie fangen.
//
// Voll brandbar (nur --pfs-color-*-Tokens).
import { defineComponent } from "../../composition/define-component.js";
import { withShadow } from "../../composition/with-shadow.js";
import { installFieldValue, bindFieldControl } from "../../composition/field-value.js";
import { CONTROL_CSS } from "../../styles/control.js";

const STYLES = `
:host { display: flex; flex-direction: column; gap: var(--pfs-space-label, 0.5rem); }
.field { display: flex; flex-direction: column; gap: var(--pfs-space-label, 0.5rem); }
.lbl { display: block; font-size: 0.875rem; font-weight: 500; color: var(--pfs-color-on-surface); }
.lbl:empty { display: none; }
.req { color: var(--pfs-color-danger); }
${CONTROL_CSS}
:host([data-error]:not([data-error=""])) input { border-color: var(--pfs-color-danger); }
.ro {
  padding: 0.5rem 0.6rem; box-sizing: border-box;
  background: var(--pfs-color-surface-sunken);
  border: 1px solid var(--pfs-color-outline);
  border-radius: var(--pfs-radius-sm, 0.375rem);
  color: var(--pfs-color-on-surface-muted); font-size: 0.9rem;
}
.msg { font-size: 0.8rem; }
.msg:empty { display: none; }
.msg.error { color: var(--pfs-color-danger); }
.msg.hint { color: var(--pfs-color-on-surface-muted); }
/* Inline-Variante (analog pfs-select-field data-inline): kompakter In-Cell-Input
   ohne Label/Message-Zeile, z.B. E-Mail-Override in einer Tabellenzelle. */
:host([data-inline]) { display: inline-block; }
:host([data-inline]) .lbl,
:host([data-inline]) .msg { display: none; }
:host([data-inline]) input { width: auto; padding: 0.2rem 0.4rem; font-size: 0.8rem; }
`;

defineComponent({
  tagName: "pfs-text-field",
  mixins: [withShadow({ mode: "open", delegatesFocus: true })],
  // .value ab Konstruktion les-/schreibbar (pre-connect-sicher, PFS-645).
  created: installFieldValue,
  render(host, ctx) {
    const root = ctx.shadowRoot;
    if (!root) return;
    const style = document.createElement("style");
    style.textContent = STYLES;
    root.appendChild(style);

    // Label umschliesst den Input → Klick aufs Label fokussiert das Control
    // (native a11y, kein for/id noetig).
    const label = document.createElement("label");
    label.className = "field";
    const lblEl = document.createElement("span");
    lblEl.className = "lbl";
    const input = document.createElement("input");
    const roEl = document.createElement("div");
    roEl.className = "ro";
    label.append(lblEl, input, roEl);
    root.appendChild(label);
    const msg = document.createElement("p");
    msg.className = "msg";
    root.appendChild(msg);

    // type VOR dem Initialwert setzen (sonst koennte ein time/date-Input den
    // Wert "08:00" verwerfen, wenn er noch type=text ist). paint() haelt es
    // danach reaktiv aktuell.
    input.type = host.dataset.type ?? "text";
    // el.value an den Input binden: wendet den gewuenschten Wert (pre-connect
    // gepuffert oder data-value) an + haelt den Puffer bei Tippen synchron.
    bindFieldControl(host, input);

    // native input/change re-emittieren (composed ueberquert die Shadow-Grenze).
    const reemit = (type: string) => (): void => {
      host.dispatchEvent(new Event(type, { bubbles: true, composed: true }));
    };
    input.addEventListener("input", reemit("input"));
    input.addEventListener("change", reemit("change"));

    const paint = (): void => {
      const labelText = host.dataset.label ?? "";
      lblEl.textContent = labelText;
      if (host.dataset.required != null && labelText) {
        const s = document.createElement("span");
        s.className = "req";
        s.textContent = "*";
        lblEl.appendChild(s);
      }

      input.type = host.dataset.type ?? "text";
      if (host.dataset.name != null) input.name = host.dataset.name;
      // autocomplete durchreichen — sonst verlieren Passwort-Formulare bei der
      // Migration die Passwort-Manager-Semantik (current-password/new-password).
      if (host.dataset.autocomplete != null) input.setAttribute("autocomplete", host.dataset.autocomplete);
      else input.removeAttribute("autocomplete");
      input.placeholder = host.dataset.placeholder ?? "";
      if (host.dataset.maxlength != null) input.maxLength = Number(host.dataset.maxlength);
      else input.removeAttribute("maxlength");
      if (host.dataset.step != null) input.step = host.dataset.step;
      else input.removeAttribute("step");
      if (host.dataset.min != null) input.min = host.dataset.min;
      else input.removeAttribute("min");
      if (host.dataset.max != null) input.max = host.dataset.max;
      else input.removeAttribute("max");

      const ro = host.dataset.readonly != null;
      input.style.display = ro ? "none" : "";
      input.disabled = ro;
      roEl.style.display = ro ? "" : "none";
      if (ro) roEl.textContent = host.dataset.value ?? input.value;

      const err = (host.dataset.error ?? "").trim();
      const hint = (host.dataset.hint ?? "").trim();
      if (err) {
        msg.textContent = err;
        msg.className = "msg error";
      } else if (hint) {
        msg.textContent = hint;
        msg.className = "msg hint";
      } else {
        msg.textContent = "";
        msg.className = "msg";
      }
    };
    paint();

    const obs = new MutationObserver(paint);
    obs.observe(host, {
      attributes: true,
      attributeFilter: [
        "data-label",
        "data-required",
        "data-error",
        "data-hint",
        "data-readonly",
        "data-type",
        "data-name",
        "data-autocomplete",
        "data-placeholder",
        "data-maxlength",
        "data-step",
        "data-min",
        "data-max",
      ],
    });
    ctx.cleanup.push(() => obs.disconnect());
  },
});
